import React, { forwardRef, useCallback, useRef } from 'react';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';

export interface TabItem {
  id: string;
  label: string;
  icon?: LucideIcon;
}

export interface TabsProps {
  /** Tab definitions */
  tabs: TabItem[];
  /** Currently active tab id */
  activeTab: string;
  /** Callback when active tab changes */
  onChange: (tabId: string) => void;
  /** Visual variant */
  variant?: 'underline' | 'pill';
  /** Additional class names */
  className?: string;
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  ({ tabs, activeTab, onChange, variant = 'underline', className }, ref) => {
    const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        const currentIndex = tabs.findIndex((t) => t.id === activeTab);
        let nextIndex: number | null = null;

        if (e.key === 'ArrowRight') {
          e.preventDefault();
          nextIndex = (currentIndex + 1) % tabs.length;
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        } else if (e.key === 'Home') {
          e.preventDefault();
          nextIndex = 0;
        } else if (e.key === 'End') {
          e.preventDefault();
          nextIndex = tabs.length - 1;
        }

        if (nextIndex !== null) {
          const nextTab = tabs[nextIndex];
          onChange(nextTab.id);
          tabRefs.current.get(nextTab.id)?.focus();
        }
      },
      [tabs, activeTab, onChange],
    );

    return (
      <div
        ref={ref}
        role="tablist"
        aria-orientation="horizontal"
        onKeyDown={handleKeyDown}
        className={clsx(
          'flex',
          variant === 'underline' && 'border-b border-[var(--volt-dark-border)] gap-0',
          variant === 'pill' && 'gap-1 p-1 rounded-[var(--volt-radius-lg)] bg-[var(--volt-dark-surface)]',
          className,
        )}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              ref={(el) => {
                if (el) {
                  tabRefs.current.set(tab.id, el);
                } else {
                  tabRefs.current.delete(tab.id);
                }
              }}
              type="button"
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onChange(tab.id)}
              className={clsx(
                'inline-flex items-center gap-2 text-sm font-medium whitespace-nowrap',
                'transition-all duration-[var(--volt-transition-fast)]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--volt-blue)]',
                variant === 'underline' && [
                  'px-4 py-2.5 -mb-px border-b-2',
                  isActive
                    ? 'border-[var(--volt-blue)] text-[var(--volt-blue)]'
                    : 'border-transparent text-[var(--volt-text-muted)] hover:text-[var(--volt-text-secondary)] hover:border-[var(--volt-dark-border-hover)]',
                ],
                variant === 'pill' && [
                  'px-3 py-1.5 rounded-[var(--volt-radius-md)]',
                  isActive
                    ? 'bg-[var(--volt-blue-muted)] text-[var(--volt-blue)]'
                    : 'text-[var(--volt-text-muted)] hover:text-[var(--volt-text-secondary)] hover:bg-[var(--volt-dark-elevated)]',
                ],
              )}
            >
              {Icon && (
                <Icon
                  className="h-4 w-4"
                  aria-hidden="true"
                />
              )}
              {tab.label}
            </button>
          );
        })}
      </div>
    );
  },
);

Tabs.displayName = 'Tabs';
